import React, { memo } from "react";
import { View, TouchableOpacity } from "react-native";
import { useRouter } from "expo-router";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";

/**
 * A single breadcrumb segment.
 *
 * - Provide `labelKey` for static i18n-translated labels.
 * - Provide `labelText` for dynamic data-driven strings (e.g. company name).
 * - Providing `href` makes the segment a tappable link that navigates to that route.
 *   Omit `href` on the last (current-page) segment.
 */
export type BreadcrumbSegment =
  | { labelKey: TranslationKeys; labelText?: never; href?: string }
  | { labelText: string; labelKey?: never; href?: string };

export interface BreadcrumbProps {
  /**
   * Ordered segments from root → current page (minimum 2).
   * The last segment is always non-interactive (current location).
   * Any preceding segment with an `href` is rendered as a tappable navigation link.
   */
  segments: [BreadcrumbSegment, BreadcrumbSegment, ...BreadcrumbSegment[]];
  testID?: string;
}

/**
 * A multi-level route-aware breadcrumb navigation row.
 *
 * Mirrors the "Breadcrumb Text" component from Figma (node 12125:14673).
 *
 * Structure:
 *   `‹  Root  ›  Ancestor  ›  Current Page`
 *
 * - Segments with `href`: `text-primary` — tappable, navigate via expo-router.
 * - Separators `›`: visual dividers between segments.
 * - Last segment: `text-supporting-text` — non-interactive, current location.
 *
 * @example
 * // Two-level (detail page)
 * <Breadcrumb
 *   segments={[
 *     { labelKey: "review.dashboard.title", href: "/(staff)/(dashboard)/applications" },
 *     { labelText: companyName },
 *   ]}
 * />
 *
 * @example
 * // Three-level (review page)
 * <Breadcrumb
 *   segments={[
 *     { labelKey: "review.dashboard.title", href: "/(staff)/(dashboard)/applications" },
 *     { labelText: companyName, href: `/(staff)/applications/${id}` },
 *     { labelKey: "review.review.title" },
 *   ]}
 * />
 */
export const Breadcrumb = memo(function Breadcrumb({ segments, testID }: BreadcrumbProps) {
  const { t } = useI18n();
  const router = useRouter();

  return (
    <View className="flex-row flex-wrap items-center gap-1 bg-white lg:px-4" testID={testID}>
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;
        const isFirst = index === 0;
        const label = segment.labelKey ? t(segment.labelKey) : (segment.labelText ?? "");

        return (
          <React.Fragment key={index}>
            {/* Forward chevron separator (between segments) */}
            {!isFirst && (
              <AppText weight="medium" className="text-base text-supporting-text leading-[1.4]">
                ›
              </AppText>
            )}

            {isLast ? (
              /* Current page — non-interactive */
              <AppText weight="medium" className="text-base text-supporting-text leading-[1.4]">
                {label}
              </AppText>
            ) : segment.href ? (
              /* Tappable ancestor link */
              <TouchableOpacity
                onPress={() => router.push(segment.href as Parameters<typeof router.push>[0])}
                accessibilityRole="link"
                accessibilityLabel={label}
                className="flex-row items-center gap-1"
              >
                {isFirst && (
                  /* Back chevron — only before the root segment */
                  <AppText weight="medium" className="text-base text-primary leading-[1.4]">
                    ‹
                  </AppText>
                )}
                <AppText weight="medium" className="text-base text-primary leading-[1.4]">
                  {label}
                </AppText>
              </TouchableOpacity>
            ) : (
              /* Non-tappable ancestor (no href) */
              <View className="flex-row items-center gap-1">
                {isFirst && (
                  <AppText weight="medium" className="text-base text-supporting-text leading-[1.4]">
                    ‹
                  </AppText>
                )}
                <AppText weight="medium" className="text-base text-supporting-text leading-[1.4]">
                  {label}
                </AppText>
              </View>
            )}
          </React.Fragment>
        );
      })}
    </View>
  );
});
